import type { FunctionComponent } from 'react';
import type { OverlayTilesItem } from './subcomponents/OverlayTiles';
import { OverlayTiles } from './subcomponents/OverlayTiles';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { GovernmentDataPartnerBannerWithData } from './subcomponents/GovernmentDataPartnerBannerWithData/GovernmentDataPartnerBannerWithData';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { Button, spacingHelper, typographyHelper } from '@property.com.au/ui-kit';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { styled } from 'styled-components';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { DynamicText, TextWithStyles } from '../../../app-kit/components/DynamicText/DynamicText';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import {
  ButtonFragment,
  mapDynamicContentItems,
  TextFragment,
  WebLinkFragment,
} from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import {
  OverlayDetails,
  OverlayDetailsType,
} from './subcomponents/DetailsOfGovernmentOverlays/OverlayDetails';

interface GovernmentOverlaysBrickProps {
  title: string;
  subtitle?: string | null;
  landDescription?: TextWithStyles[] | null;
  overlayTiles?: OverlayTilesItem[] | null;
  overlayDetails: OverlayDetailsType | null;
  moreInformation?: (ButtonFragment | TextFragment | WebLinkFragment)[] | null;
}

const LandDescription = styled.div`
  margin-bottom: ${spacingHelper('medium')};
`;

const MoreInformationWrapper = styled.div`
  margin-bottom: ${spacingHelper('small')};
  line-height: 1.125rem;
`;

export const MoreInformation = styled(DynamicContent)`
  ${typographyHelper('body02')}
`;

const DisclaimerLinkButton = styled(Button)`
  ${typographyHelper('caption')}
  text-align: left;
  padding-top: ${spacingHelper('medium')};
`;

export const GovernmentOverlaysBrick: FunctionComponent<GovernmentOverlaysBrickProps> = ({
  title,
  subtitle,
  landDescription,
  overlayTiles,
  overlayDetails,
  moreInformation,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle || undefined}
      headerChildren={
        <DisclaimerIcon
          title="Open "
          onClick={() => {
            displayModal({ name: 'governmentOverlaysBrickInformation' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__government_overlays_brick___info_button',
            });
          }}
        />
      }
    >
      {landDescription && (
        <LandDescription>
          <DynamicText
            dynamicTexts={landDescription}
            variant="body01"
            variantBold="body01Bold"
            colorVariant="textSecondary"
            colorVariantBold="textSecondary"
          />
        </LandDescription>
      )}
      {overlayTiles && <OverlayTiles tiles={overlayTiles} />}
      {overlayDetails && (
        <OverlayDetails
          title={overlayDetails.title}
          description={overlayDetails.description}
          overlayBreakdown={overlayDetails.overlayBreakdown}
        />
      )}
      {moreInformation && (
        <MoreInformationWrapper>
          <MoreInformation
            dynamicContents={mapDynamicContentItems(moreInformation)}
            textColorVariant="textSecondary"
          />
        </MoreInformationWrapper>
      )}
      <GovernmentDataPartnerBannerWithData />
      <DisclaimerLinkButton
        variant="link-primary"
        onClick={() => {
          displayModal({ name: 'governmentOverlaysBrickDataDisclaimer' });
          trackDisclaimerModalOpen({
            inPageSource: 'property_page__government_overlays_brick___disclaimer_button',
          });
        }}
      >
        About this data
      </DisclaimerLinkButton>
    </ContentBrick>
  );
};
